import React, { useState, useEffect, useRef } from 'react';
import './CoursePolicy.css';
import './styles/mobile.css';
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  AppBar,
  Toolbar,
  Box,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Document, Page } from 'react-pdf';
import { CoursePolicyProps } from './CoursePolicy.props';
import { useAppSelector } from '../../redux/hooks';
import { acceptCoursePolicyEvents, coursePolicyEventsDataSelector } from '../../redux/coursePolicySlice';
import { COURSE_POLICY_STATUS, HAS_VALUES } from '../../constants/constants';
import { useDispatch } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import {
  trainingCourseDetailEvents
} from '../../redux/trainingCourseDetailSlice';

export const CoursePolicy: React.FC<CoursePolicyProps> = (props) => {
  const { open, onClose, phishId, trainingResultId, campaignId } = props
  const dispatch = useDispatch<any>();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [isAcceptEnabled, setIsAcceptEnabled] = useState(false)
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [coursePolicyStatus, setCoursePolicyStatus] = useState<number | undefined>(undefined);
  const [renderedPages, setRenderedPages] = useState<number>(0);
  const { coursePolicyData, isLoading } = useAppSelector(coursePolicyEventsDataSelector);
  const handleDownload = async () => {
    try {
      const response = await fetch(coursePolicyData.coursePolicyUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
  
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = 'CoursePolicy.pdf';
      downloadLink.style.display = 'none';
  
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.appendChild(downloadLink);
  
      // Clean up
      downloadLink.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  useEffect(() => {
    setCoursePolicyStatus(props.coursePolicyStatus);
  }, [props])

  const handleAcceptCoursePolicy = async () => {
    const params = {
      phish_id: phishId,
      training_result_id: trainingResultId
    }
    const response = await dispatch(acceptCoursePolicyEvents(params));

    if(response.payload?.isAccept) {
      setIsAcceptEnabled(false);
      setCoursePolicyStatus(COURSE_POLICY_STATUS.ACCEPTED);
    }
  }

  useEffect(() => {
    const contentCoursePolicy = contentRef.current;
    if (!contentCoursePolicy) return;

    const handleScrollCoursePolicy = () => {
      const isBottom = contentCoursePolicy.scrollTop + contentCoursePolicy.clientHeight >= contentCoursePolicy.scrollHeight - 10;

      if(isBottom && !isAcceptEnabled && coursePolicyStatus != COURSE_POLICY_STATUS.ACCEPTED) {
        setIsAcceptEnabled(true);
      }
    };

    if(open) {
      contentCoursePolicy.addEventListener('scroll', handleScrollCoursePolicy);
    } else {
      // Reset numPage and isAcceptEnabled when close course policy dialog
      const data = {
        campaign_id: campaignId
      };
      dispatch(trainingCourseDetailEvents(data));
    }

    return () => {
      contentCoursePolicy.removeEventListener('scroll', handleScrollCoursePolicy);
    };
  }, [numPages, open, isAcceptEnabled]);

  useEffect(() => {
    if (renderedPages === numPages && numPages !== null) {
      const el = contentRef.current;
      if (el) {
        const isScrollable = el.scrollHeight > el.clientHeight;
        if (!isScrollable && !coursePolicyData?.isAccept && coursePolicyStatus !== COURSE_POLICY_STATUS.ACCEPTED) {
          setIsAcceptEnabled(true);
        }
      }
    }
  }, [renderedPages, numPages, coursePolicyData?.isAccept, coursePolicyStatus]);

  const handleClose =()=>{
    setIsAcceptEnabled(false);
    setNumPages(null);
    setRenderedPages(0);
    onClose()
  }
  
  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      {isLoading && (
        <Box className='loading-icon'>
          <CircularProgress />
        </Box>
      )}

      <Box className='course-policy'>
        <AppBar className='course-policy-header'>
            <Toolbar sx={{ justifyContent: 'flex-end' }}>
              <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
                <CloseIcon htmlColor='black' />
              </IconButton>
            </Toolbar>
        </AppBar>

        <DialogContent
          ref={contentRef}
          dividers
          className='course-policy-body-container'
        >
          <Box className='course-policy-body'>
            <Document
              file={coursePolicyData?.coursePolicyUrl}
              onLoadSuccess={({ numPages }) => {
                setNumPages(numPages)
                setErrorMsg(null);
              }}
              onLoadError={(error) => {
                console.error('Course policy load error:', error);
                setErrorMsg('Failed to load course policy.');
              }}
              loading={<Typography>Loading course policy</Typography>}
              error={<></>}
            >
              {!errorMsg && 
                Array.from(new Array(numPages), (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={800}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    onRenderSuccess={() => {
                      setRenderedPages((prev) => prev + 1);
                    }}
                  />
              ))}
            </Document>

            {errorMsg && (
              <Typography color="error" mt={2}>
                {errorMsg}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            variant="contained" 
            onClick={handleAcceptCoursePolicy}
            disabled={isLoading || !isAcceptEnabled || coursePolicyStatus == COURSE_POLICY_STATUS.ACCEPTED}
          >
              Accept
          </Button>
          {props.coursePolicyEnableDownload === HAS_VALUES.TRUE && (
            <Button variant="outlined" onClick={handleDownload}>
              Download Policy
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};